/**
 * LEGACY experience (stores not on the Service Desk pilot list): Confluence help
 * articles and Jira report-an-issue wiring, kept as it was on main.
 *
 * Help + report-an-issue wiring for The Sweet Spot.
 *
 * The panel itself is the shared `HelpPanel` from `@ampersandbrands/ui`; this
 * file is the app's half of the contract: which audience to ask for, how to
 * reach the API, and what the store can report on.
 *
 * Audience matters. The same endpoint serves Cockpit, so without
 * `?audience=sweetspot` a store employee would be handed articles about HQ
 * tools they cannot open.
 */

import { useMutation } from '@tanstack/react-query';
import type { HelpArticle, HelpArticleSummary, HelpClient, ReportIssueKind } from '@ampersandbrands/ui';
import { apiGet, apiPost, API_BASE } from './api';

/** One "name: value" line the api renders under the ticket's Page state
 * section, for app state that lives outside the URL (e.g. the corporate
 * store picker, which is kept in localStorage, not the query string). */
export type PageStateEntry = { label: string; value: string };

const HELP_BASE = `${API_BASE}/v1/core/help`;

export const helpClient: HelpClient = {
  async listArticles(): Promise<HelpArticleSummary[]> {
    const res = await apiGet<{ articles: HelpArticleSummary[] }>(
      `${HELP_BASE}/articles?audience=sweetspot`,
    );
    return res.articles ?? [];
  },
  async getArticle(pageId: string): Promise<HelpArticle> {
    const article = await apiGet<HelpArticle>(
      `${HELP_BASE}/articles/${encodeURIComponent(pageId)}`,
    );
    // Article images are served by the API as root-relative paths. The app runs
    // on a different host, so they need the API origin or they 404.
    return {
      ...article,
      html: article.html.replaceAll('src="/v1/core/help/', `src="${API_BASE}/v1/core/help/`),
    };
  },
};

/**
 * The KB article for each part of the app, so the help panel opens on what the
 * reader is actually looking at instead of the index. Ids are Confluence page
 * ids in ITCORPKB under The Sweet Spot; Cockpit does the same thing through
 * `helpKbPageId` on its tool catalog.
 */
const ARTICLE_BY_PREFIX: ReadonlyArray<readonly [string, string]> = [
  ['/today', '113246241'],
  ['/inventory', '113344538'], // covers the count session and variance sub-pages
  ['/pulse', '113246266'],
  ['/sampling', '113016887'],
  ['/performance', '113377329'],
  ['/promotions', '113442897'],
  ['/resources', '113344626'],
  ['/loyalty', '113442927'],
  ['/recognition', '113344659'],
  ['/my-store', '113442953'],
  ['/directory', '113016930'],
  ['/changelog', '143753217'], // What's new
];

/** Article to open for a route, or null on Home and anywhere unmapped (index). */
export function helpArticleForPath(pathname: string): string | null {
  const p = pathname.toLowerCase();
  for (const [prefix, pageId] of ARTICLE_BY_PREFIX) {
    if (p === prefix || p.startsWith(`${prefix}/`)) return pageId;
  }
  return null;
}

/** What a store can report a problem about, in the app's own words. */
export const SWEET_SPOT_TOOL_OPTIONS = [
  'Home',
  'Today',
  'Inventory',
  'Pulse',
  'Sampling',
  'Performance',
  'Promotions',
  'Loyalty',
  'Recognition',
  'Resources',
  'My Store',
  'Directory',
  'Chat',
  'Sign in / access',
  'The tablet itself',
  'Something else',
] as const;

/** Route to the tool name the report form should preselect. */
export function toolLabelForPath(pathname: string): string | undefined {
  const p = pathname.toLowerCase();
  if (p === '/' || p === '') return 'Home';
  if (p.startsWith('/today')) return 'Today';
  if (p.startsWith('/inventory')) return 'Inventory';
  if (p.startsWith('/pulse')) return 'Pulse';
  if (p.startsWith('/sampling')) return 'Sampling';
  if (p.startsWith('/performance')) return 'Performance';
  if (p.startsWith('/promotions')) return 'Promotions';
  if (p.startsWith('/loyalty')) return 'Loyalty';
  if (p.startsWith('/recognition')) return 'Recognition';
  if (p.startsWith('/resources')) return 'Resources';
  if (p.startsWith('/my-store')) return 'My Store';
  if (p.startsWith('/directory')) return 'Directory';
  return undefined;
}

/**
 * Submits a KB-article feedback instruction ("this article is missing X"),
 * tied to the exact article, for the docs owner to act on. Gated server-side
 * by the `admin.kb-feedback` accessSlug; the caller (HelpAndSupport.tsx)
 * already checked `canSubmit` before rendering the box, but the API is the
 * real gate regardless.
 */
export async function submitKbFeedback(input: {
  pageId: string;
  articleTitle: string;
  instruction: string;
  route: string;
}): Promise<void> {
  await apiPost(`${HELP_BASE}/articles/${encodeURIComponent(input.pageId)}/feedback`, {
    instruction: input.instruction,
    article_title: input.articleTitle,
    app: 'sweetspot',
    route: input.route,
  });
}

/**
 * One file on its way to the ticket. Same shape as the shared dialog's
 * `ReportIssueAttachment` and as the api's `attachments[]` entries; kept as
 * its own type here so this module states the request contract in full.
 *
 * `content_type` is a loose string because the api owns the allowlist: it
 * rejects a bad type by name, which is a better message than the browser
 * guessing on our side.
 */
export interface SupportAttachment {
  filename: string;
  content_type: string;
  base64: string;
}

export interface SupportIssueInput {
  kind: ReportIssueKind;
  tool?: string;
  summary: string;
  description: string;
  /** The description as the rich-text editor produced it. `description` stays
   * the plain-text fallback the ticket is created with. */
  description_html?: string;
  priority: string;
  route?: string;
  /** The single-file shape, kept for the case where nothing was attached.
   * Widened to {@link SupportAttachment} because the dialog now hands back
   * whatever type the reporter's file carried, and the api is what checks it.
   * The narrower shape our own screen capture produces still fits. */
  screenshot?: SupportAttachment | null;
  /** Everything the reporter attached, in the order the dialog listed it.
   * Inline images in `description_html` are marked with a
   * `data-attachment-index` pointing at a position in this list. */
  attachments?: SupportAttachment[];
  page_state?: PageStateEntry[];
}

export interface SupportTicket {
  key: string;
  url: string;
  warnings?: string[];
}

/**
 * Files the ticket. The reporter is resolved server-side: a store signs in with
 * its lolliandpops.com Google account, and the API maps that to the store's
 * ampersandbrands.com address before raising the request, so tickets come from
 * an identity the help desk can reply to.
 */
export function useCreateSupportIssue() {
  return useMutation<{ ticket: SupportTicket }, Error, SupportIssueInput>({
    mutationFn: (input) => {
      // Send the many-file shape or the single-file one, never both.
      //
      // The api builds one list as `screenshot` first, then `attachments`, and
      // numbers it from zero. Those numbers are what `data-attachment-index`
      // in the description points at. The dialog already seeds the
      // auto-captured screenshot as the first attachment and derives
      // `screenshot` from that same list, so sending both would attach the
      // first image twice and shift every inline image one place along.
      const attachments = input.attachments ?? [];
      const files =
        attachments.length > 0
          ? { attachments }
          : { screenshot: input.screenshot ?? undefined };

      return apiPost<{ ticket: SupportTicket }>(`${API_BASE}/v1/core/support/issue`, {
        product: 'sweet-spot',
        kind: input.kind,
        tool: input.tool,
        summary: input.summary,
        description: input.description,
        description_html: input.description_html,
        priority: input.priority,
        route: input.route,
        ...files,
        url: window.location.href,
        page_title: document.title,
        page_state: input.page_state,
        client_context: {
          timestamp: new Date().toISOString(),
          user_agent: window.navigator.userAgent,
        },
      });
    },
  });
}
